import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Edit3, 
  Save, 
  X, 
  Plus,
  Trash2,
  MessageCircle,
  HelpCircle,
  CheckCircle,
  Car,
  GraduationCap,
  Briefcase,
  Star,
  Calendar
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AppNavigation from "./AppNavigation";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const AreaProfiloCandidato = ({ candidatoId, onClose }) => {
  const [candidato, setCandidato] = useState(null);
  const [cvUrl, setCvUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const token = localStorage.getItem('token');
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({});
  const [nuovaCompetenza, setNuovaCompetenza] = useState("");
  const [nuovaNota, setNuovaNota] = useState("");
  const [showAssistenza, setShowAssistenza] = useState(false);
  const [messaggiAssistenza, setMessaggiAssistenza] = useState("");

  useEffect(() => {
    if (candidatoId) {
      loadCandidatoData();
    }
  }, [candidatoId]);

  const loadCandidatoData = async () => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/v1/candidato/${candidatoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const candidatoData = response.data;
      setCandidato(candidatoData);
      setFormData({
        nome: candidatoData.nome || "",
        cognome: candidatoData.cognome || "",
        telefono: candidatoData.telefono || "",
        email: candidatoData.email || "",
        citta: candidatoData.citta || "",
        zona_preferenza_lavorativa: candidatoData.zona_preferenza_lavorativa || "",
        esperienza_immobiliare: candidatoData.esperienza_immobiliare || "nessuna",
        tipo_impiego_preferito: candidatoData.tipo_impiego_preferito || "",
        mobilita_trasporto: candidatoData.mobilita_trasporto || "",
        competenze: candidatoData.competenze || [],
        note_personali: candidatoData.note_personali || []
      });
      // Se il CV è presente, imposta l'URL per la preview
      if (candidatoData.cv_pdf_url) {
        setCvUrl(candidatoData.cv_pdf_url);
      } else {
        setCvUrl(null);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      console.error("Error loading candidato:", error);
      toast.error("Errore nel caricamento dati profilo");
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleSaveSection = async (section) => {
    try {
      let updateData = {};
      
      if (section === 'dati_personali') {
        updateData = {
          nome: formData.nome,
          cognome: formData.cognome,
          telefono: formData.telefono,
          email: formData.email,
          citta: formData.citta
        };
      } else if (section === 'profilo_professionale') {
        updateData = {};
        if (formData.zona_preferenza_lavorativa) updateData.zona_preferenza_lavorativa = formData.zona_preferenza_lavorativa;
        if (formData.esperienza_immobiliare) updateData.esperienza_immobiliare = formData.esperienza_immobiliare;
        if (formData.tipo_impiego_preferito) updateData.tipo_impiego_preferito = formData.tipo_impiego_preferito;
        if (formData.mobilita_trasporto) updateData.mobilita_trasporto = formData.mobilita_trasporto;
      } else if (section === 'competenze') {
        updateData = {
          competenze: formData.competenze
        };
      } else if (section === 'note_personali') {
        updateData = {
          note_personali: formData.note_personali
        };
      }

      await axios.put(`${API}/api/v1/candidato/${candidatoId}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Aggiorna lo stato locale
      setCandidato(prev => ({ ...prev, ...updateData }));
      setEditingSection(null);
      
      toast.success("Profilo aggiornato con successo!");
    } catch (error) {
      console.error("Error updating candidato:", error);
      toast.error("Errore nell'aggiornamento del profilo");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const aggiungiCompetenza = () => {
    if (nuovaCompetenza.trim()) {
      const nuoveCompetenze = [...(formData.competenze || []), nuovaCompetenza.trim()];
      setFormData(prev => ({ ...prev, competenze: nuoveCompetenze }));
      setNuovaCompetenza("");
    }
  };

  const rimuoviCompetenza = (index) => {
    const nuoveCompetenze = formData.competenze.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, competenze: nuoveCompetenze }));
  };

  const aggiungiNota = () => {
    if (nuovaNota.trim()) {
      const nuoveNote = [...(formData.note_personali || []), {
        id: Date.now().toString(),
        testo: nuovaNota.trim(),
        data: new Date().toISOString().split('T')[0]
      }];
      setFormData(prev => ({ ...prev, note_personali: nuoveNote }));
      setNuovaNota("");
    }
  };

  const rimuoviNota = (noteId) => {
    const nuoveNote = formData.note_personali.filter(nota => nota.id !== noteId);
    setFormData(prev => ({ ...prev, note_personali: nuoveNote }));
  };

  const handleAssistenza = async () => {
    if (!messaggiAssistenza.trim()) {
      toast.error("Inserisci un messaggio per richiedere assistenza");
      return;
    }

    try {
      // Qui andrebbe implementata la logica per inviare il messaggio al team
      // Per ora simuliamo l'invio
      console.log("Messaggio assistenza:", messaggiAssistenza);
      
      toast.success("Richiesta di assistenza inviata al team InLeads!");
      setMessaggiAssistenza("");
      setShowAssistenza(false);
    } catch (error) {
      console.error("Error sending assistance request:", error);
      toast.error("Errore nell'invio della richiesta");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Caricamento profilo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Il Mio Profilo</h1>
            <p className="text-gray-600">Gestisci le tue informazioni personali e professionali</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => window.location.reload()}
            className="rounded-xl"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-left w-4 h-4 mr-2" aria-hidden="true"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
            Indietro
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="rounded-xl"
          >
            <X className="w-4 h-4 mr-2" />
            Chiudi
          </Button>
        </div>
      </div>

      {/* Sezione modifica profilo */}
      {editingSection === "dati_personali" && (
        <>
          <div className="flex items-center mb-4">
            <Button
              className="bg-gray-200 text-gray-800 rounded-xl mr-2"
              onClick={() => window.location.reload()}
            >
              ← Indietro
            </Button>
          </div>
          <div className="bg-white rounded-xl shadow p-6 mt-2">
            <h2 className="text-xl font-bold mb-4">Modifica Profilo</h2>
            {/* Upload CV anche qui */}
            <div className="mb-4">
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const fileInput = document.getElementById("cv-pdf-upload-edit");
                  const file = fileInput.files[0];
                  if (!file) return toast.error("Seleziona un file PDF!");
                  if (file.type !== "application/pdf") return toast.error("Sono accettati solo file PDF");
                  const fd = new FormData();
                  fd.append("cv_pdf", file);
                  try {
                    const res = await axios.post(
                      `${API}/api/v1/candidato/${candidatoId}/upload-cv`,
                      fd,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "multipart/form-data",
                        },
                      }
                    );
                    toast.success("CV caricato con successo!");
                    fileInput.value = "";
                    // Aggiorna la preview del CV
                    if (res.data && res.data.cv_pdf_url) {
                      setCvUrl(res.data.cv_pdf_url);
                    } else {
                      // fallback: ricarica dati candidato
                      loadCandidatoData();
                    }
                  } catch (err) {
                    toast.error("Errore nel caricamento del CV");
                  }
                }}
              >
                <label htmlFor="cv-pdf-upload-edit" className="block text-sm font-medium text-gray-700 mb-2">
                  Aggiungi il tuo CV (PDF)
                </label>
                <div className="flex gap-2 items-center">
                  <input type="file" id="cv-pdf-upload-edit" accept="application/pdf" className="border rounded px-2 py-1" />
                  <Button type="submit" className="bg-blue-600 text-white rounded-xl">Carica CV PDF</Button>
                </div>
              </form>
            </div>
            {/* Preview CV PDF se presente */}
            {cvUrl && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Anteprima CV caricato</label>
                <iframe
                  src={cvUrl}
                  title="Anteprima CV PDF"
                  width="100%"
                  height="400px"
                  className="border rounded"
                />
              </div>
            )}
            {/* Campi di modifica profilo */}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await axios.put(
                    `${API}/api/v1/candidato/${candidatoId}`,
                    formData,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );
                  toast.success("Profilo aggiornato!");
                  setEditingSection(null);
                } catch (err) {
                  toast.error("Errore nel salvataggio del profilo");
                }
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input
                    type="text"
                    className="border rounded px-2 py-1 w-full"
                    value={formData.nome || ""}
                    onChange={e => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="border rounded px-2 py-1 w-full"
                    value={formData.email || ""}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Telefono</label>
                  <input
                    type="text"
                    className="border rounded px-2 py-1 w-full"
                    value={formData.telefono || ""}
                    onChange={e => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Città</label>
                  <input
                    type="text"
                    className="border rounded px-2 py-1 w-full"
                    value={formData.citta || ""}
                    onChange={e => setFormData(prev => ({ ...prev, citta: e.target.value }))}
                  />
                </div>
                {/* Aggiungi altri campi se necessario */}
              </div>
              <div className="mt-6 flex gap-2 justify-end">
                <Button type="button" className="bg-gray-400 text-white rounded-xl" onClick={() => setEditingSection(null)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-blue-600 text-white rounded-xl">
                  Salva Profilo
                </Button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Preview CV PDF in visualizzazione profilo */}
      {cvUrl && (
        <Card className="border-0 shadow-lg rounded-2xl mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              CV Caricato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <iframe
              src={cvUrl}
              title="Anteprima CV PDF"
              width="100%"
              height="400px"
              className="border rounded"
            />
          </CardContent>
        </Card>
      )}
      {/* Dati Personali */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Dati Personali
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingSection(editingSection === 'dati_personali' ? null : 'dati_personali')}
              className="rounded-xl"
            >
              {editingSection === 'dati_personali' ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {editingSection === 'dati_personali' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => handleInputChange('nome', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cognome</label>
                  <input
                    type="text"
                    value={formData.cognome}
                    onChange={(e) => handleInputChange('cognome', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleInputChange('telefono', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Città</label>
                  <input
                    type="text"
                    value={formData.citta}
                    onChange={(e) => handleInputChange('citta', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>
              </div>
              <Button
                onClick={() => handleSaveSection('dati_personali')}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva Modifiche
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Nome Completo</p>
                  <p className="font-semibold">{candidato?.nome} {candidato?.cognome}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Telefono</p>
                  <p className="font-semibold">{candidato?.telefono || "Non specificato"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold">{candidato?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Città</p>
                  <p className="font-semibold">{candidato?.citta || "Non specificata"}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profilo Professionale */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Profilo Professionale
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingSection(editingSection === 'profilo_professionale' ? null : 'profilo_professionale')}
              className="rounded-xl"
            >
              {editingSection === 'profilo_professionale' ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {editingSection === 'profilo_professionale' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zona Preferenza Lavorativa</label>
                <input
                  type="text"
                  value={formData.zona_preferenza_lavorativa}
                  onChange={(e) => handleInputChange('zona_preferenza_lavorativa', e.target.value)}
                  placeholder="Es: Milano centro, Roma nord..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Esperienza Immobiliare</label>
                <select
                  value={formData.esperienza_immobiliare}
                  onChange={(e) => handleInputChange('esperienza_immobiliare', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="nessuna">Nessuna esperienza</option>
                  <option value="meno_1_anno">Meno di 1 anno</option>
                  <option value="da_1_a_3_anni">Da 1 a 3 anni</option>
                  <option value="piu_3_anni">Più di 3 anni</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo Impiego Preferito</label>
                <select
                  value={formData.tipo_impiego_preferito}
                  onChange={(e) => handleInputChange('tipo_impiego_preferito', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                >
                  <option value="">Seleziona...</option>
                  <option value="part_time">Part Time</option>
                  <option value="full_time">Full Time</option>
                  <option value="consulenza">Consulenza</option>
                  <option value="collaborazione">Collaborazione</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobilità e Trasporto</label>
                <select
                  value={formData.mobilita_trasporto}
                  onChange={(e) => handleInputChange('mobilita_trasporto', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                >
                  <option value="">Seleziona...</option>
                  <option value="auto_propria">Auto Propria</option>
                  <option value="mezzi_pubblici">Mezzi Pubblici</option>
                  <option value="bicicletta">Bicicletta</option>
                  <option value="moto">Moto/Scooter</option>
                </select>
              </div>
              <Button
                onClick={() => handleSaveSection('profilo_professionale')}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva Modifiche
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Zona Preferenza</p>
                  <p className="font-semibold">{candidato?.zona_preferenza_lavorativa || "Non specificata"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Esperienza Immobiliare</p>
                  <Badge variant={candidato?.esperienza_immobiliare && candidato?.esperienza_immobiliare !== "nessuna" ? "default" : "secondary"}>
                    {candidato?.esperienza_immobiliare === "nessuna" ? "Nessuna esperienza" :
                     candidato?.esperienza_immobiliare === "meno_1_anno" ? "Meno di 1 anno" :
                     candidato?.esperienza_immobiliare === "da_1_a_3_anni" ? "Da 1 a 3 anni" :
                     candidato?.esperienza_immobiliare === "piu_3_anni" ? "Più di 3 anni" :
                     "Non specificata"}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Tipo Impiego</p>
                  <p className="font-semibold">{candidato?.tipo_impiego_preferito || "Non specificato"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Car className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Mobilità</p>
                  <p className="font-semibold">{candidato?.mobilita_trasporto || "Non specificata"}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Competenze */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-blue-600" />
              Competenze e Abilità
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingSection(editingSection === 'competenze' ? null : 'competenze')}
              className="rounded-xl"
            >
              {editingSection === 'competenze' ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {editingSection === 'competenze' ? (
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={nuovaCompetenza}
                  onChange={(e) => setNuovaCompetenza(e.target.value)}
                  placeholder="Es: Patente B, Inglese fluente..."
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  onKeyPress={(e) => e.key === 'Enter' && aggiungiCompetenza()}
                />
                <Button
                  onClick={aggiungiCompetenza}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(formData.competenze || []).map((competenza, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-2 px-3 py-2">
                    {competenza}
                    <button
                      onClick={() => rimuoviCompetenza(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Button
                onClick={() => handleSaveSection('competenze')}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva Competenze
              </Button>
            </div>
          ) : (
            <div>
              {(candidato?.competenze || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {candidato.competenze.map((competenza, index) => (
                    <Badge key={index} variant="outline" className="px-3 py-2">
                      {competenza}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">Nessuna competenza aggiunta</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note Personali */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-600" />
              Note Personali e Obiettivi
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingSection(editingSection === 'note_personali' ? null : 'note_personali')}
              className="rounded-xl"
            >
              {editingSection === 'note_personali' ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {editingSection === 'note_personali' ? (
            <div className="space-y-4">
              <div className="flex gap-2">
                <textarea
                  value={nuovaNota}
                  onChange={(e) => setNuovaNota(e.target.value)}
                  placeholder="Es: Voglio migliorare la comunicazione, Sto cercando lavoro a Milano..."
                  rows={3}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
                />
                <Button
                  onClick={aggiungiNota}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl self-start"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-3">
                {(formData.note_personali || []).map((nota, index) => (
                  <div key={nota.id || index} className="bg-yellow-50 rounded-xl p-4 flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-gray-800">{nota.testo}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {nota.data}
                      </p>
                    </div>
                    <button
                      onClick={() => rimuoviNota(nota.id)}
                      className="text-red-500 hover:text-red-700 ml-3"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => handleSaveSection('note_personali')}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva Note
              </Button>
            </div>
          ) : (
            <div>
              {(candidato?.note_personali || []).length > 0 ? (
                <div className="space-y-3">
                  {candidato.note_personali.map((nota, index) => (
                    <div key={nota.id || index} className="bg-yellow-50 rounded-xl p-4">
                      <p className="text-gray-800">{nota.testo}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {nota.data}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">Nessuna nota personale aggiunta</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Richiedi Assistenza */}
      <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-green-50 to-green-100 border-green-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <HelpCircle className="w-5 h-5" />
            Richiedi Assistenza
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!showAssistenza ? (
            <div className="text-center">
              <p className="text-green-700 mb-4">
                Hai bisogno di aiuto o hai domande? Il nostro team è qui per te!
              </p>
              <Button
                onClick={() => setShowAssistenza(true)}
                className="bg-green-600 hover:bg-green-700 text-white rounded-xl"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Contatta il Team InLeads
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <textarea
                value={messaggiAssistenza}
                onChange={(e) => setMessaggiAssistenza(e.target.value)}
                placeholder="Descrivi il tuo problema o la tua domanda..."
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-green-200 focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none resize-none"
              />
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowAssistenza(false)}
                  variant="outline"
                  className="flex-1 rounded-xl border-gray-300"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleAssistenza}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Invia Richiesta
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AreaProfiloCandidato;